// Autoguardado de la ficha del presupuesto (formato nuevo).
//
// Una cola por presupuesto: cada bloque (cabecera, líneas, logística, Quién va,
// material...) programa su guardado con una clave. Reglas:
// - Retardo: se guarda 1,5 s después del último cambio de ese bloque (o ya, con
//   `inmediato` o con `guardarYa()`: al perder el foco, al cambiar de pestaña,
//   al salir o con el botón Guardar).
// - Nunca dos guardados a la vez: se ejecutan de uno en uno. Si llega otro
//   cambio del mismo bloque mientras se guarda, se guarda después una sola vez
//   con el último estado.
// - Campos del documento (`programarCampos`): se acumulan por bloque (gana el
//   último valor de cada campo) y se envían SOLO esos campos (PUT parcial).
// - Sin conexión (sin respuesta o error 5xx): se conserva lo pendiente y se
//   reintenta cada 10 s. Error de datos (4xx): se avisa con el mensaje del
//   servidor y el bloque queda "no guardado" (sin reintentos) hasta el siguiente
//   cambio.
// - Al salir de la página: `vaciarUrgente()` manda con `keepalive` los campos
//   pendientes y devuelve si queda algo que no se ha podido mandar (para que el
//   navegador pregunte antes de salir).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const RETARDO_AUTOGUARDADO = 1500;
export const REINTENTO_AUTOGUARDADO = 10000;

// Sin respuesta del servidor, error 5xx, tiempo agotado o demasiadas peticiones
export const esErrorDeConexion = (error) => {
  const estado = error?.response?.status;
  if (!estado) return true;
  return estado >= 500 || estado === 408 || estado === 429;
};

export const mensajeDeError = (error) => {
  const detalle = error?.response?.data?.detail;
  if (typeof detalle === 'string' && detalle.trim()) return detalle;
  if (Array.isArray(detalle) && detalle.length > 0) {
    return detalle.map(d => (d && d.msg) || String(d)).join('. ');
  }
  return error?.message || 'Error desconocido';
};

const useAutoguardado = ({
  enviarCampos,          // async (campos) => respuesta (PUT parcial)
  enviarCamposUrgente,   // (campos) => boolean (enviado con keepalive)
  onError,               // (clave, error, mensaje) error de datos (4xx)
  retardo = RETARDO_AUTOGUARDADO,
  reintento = REINTENTO_AUTOGUARDADO,
} = {}) => {
  const entradasRef = useRef(new Map()); // clave -> { tarea, listo, timer, esperas: [] }
  const camposRef = useRef(new Map());   // bloque -> { campos, conservar }
  const enCursoRef = useRef(null);       // { clave, campos } que se está guardando
  const corriendoRef = useRef(false);
  const reintentoRef = useRef(null);
  const vacioRef = useRef([]);           // resolvers de guardarYa()
  const montadoRef = useRef(true);
  const erroresRef = useRef({});
  const sinConexionRef = useRef(false);
  const ultimoRef = useRef(null);
  const opcionesRef = useRef({});
  opcionesRef.current = { enviarCampos, enviarCamposUrgente, onError, retardo, reintento };

  const [estado, setEstado] = useState({ estado: 'inactivo', ultimo: null, errores: {} });

  const publicar = useCallback(() => {
    if (!montadoRef.current) return;
    const hayEntradas = entradasRef.current.size > 0;
    let e = 'inactivo';
    if (sinConexionRef.current) e = 'sin_conexion';
    else if (corriendoRef.current) e = 'guardando';
    else if (hayEntradas) e = 'pendiente';
    else if (Object.keys(erroresRef.current).length > 0) e = 'error';
    else if (ultimoRef.current) e = 'guardado';
    setEstado({ estado: e, ultimo: ultimoRef.current, errores: { ...erroresRef.current } });
  }, []);

  const resolverVacio = useCallback(() => {
    if (corriendoRef.current) return;
    const quedanListas = [...entradasRef.current.values()].some(e => e.listo);
    if (quedanListas && !sinConexionRef.current) return;
    const esperas = vacioRef.current;
    vacioRef.current = [];
    esperas.forEach(r => r(!sinConexionRef.current && entradasRef.current.size === 0));
  }, []);

  const procesar = useCallback(async () => {
    if (corriendoRef.current) return;
    corriendoRef.current = true;
    publicar();
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const siguiente = [...entradasRef.current.entries()].find(([, e]) => e.listo);
        if (!siguiente) break;
        const [clave, entrada] = siguiente;
        entradasRef.current.delete(clave);
        try {
          const resultado = await entrada.tarea();
          delete erroresRef.current[clave];
          sinConexionRef.current = false;
          ultimoRef.current = new Date();
          entrada.esperas.forEach(w => w.resolve(resultado));
        } catch (error) {
          if (esErrorDeConexion(error)) {
            // Se conserva para el reintento (si ya hay otra más nueva, esa manda)
            const nueva = entradasRef.current.get(clave);
            if (nueva) {
              nueva.esperas.push(...entrada.esperas.splice(0));
            } else {
              entradasRef.current.set(clave, { ...entrada, listo: false, timer: null, esperas: [] });
            }
            entrada.esperas.forEach(w => w.resolve({ data: undefined, pendiente: true }));
            sinConexionRef.current = true;
            if (!reintentoRef.current) {
              reintentoRef.current = setTimeout(() => {
                reintentoRef.current = null;
                entradasRef.current.forEach((e) => { e.listo = true; });
                procesar();
              }, opcionesRef.current.reintento);
            }
            break;
          }
          const mensaje = mensajeDeError(error);
          erroresRef.current[clave] = mensaje;
          // eslint-disable-next-line no-param-reassign
          error.autoguardadoNotificado = true;
          if (opcionesRef.current.onError) opcionesRef.current.onError(clave, error, mensaje);
          entrada.esperas.forEach(w => w.reject(error));
        }
      }
    } finally {
      corriendoRef.current = false;
      publicar();
      resolverVacio();
    }
  }, [publicar, resolverVacio]);

  // Programa (o reprograma) el guardado de un bloque. Devuelve una promesa que
  // se resuelve cuando se guarda ese bloque (con la respuesta de la tarea, o
  // { pendiente: true } si no hay conexión y queda para el reintento) y se
  // rechaza si el servidor lo rechaza (4xx).
  const programar = useCallback((clave, tarea, { inmediato = false } = {}) => new Promise((resolve, reject) => {
    const anterior = entradasRef.current.get(clave);
    if (anterior && anterior.timer) clearTimeout(anterior.timer);
    const entrada = {
      tarea,
      listo: false,
      timer: null,
      esperas: [...(anterior ? anterior.esperas : []), { resolve, reject }],
    };
    delete erroresRef.current[clave];
    entradasRef.current.set(clave, entrada);
    if (inmediato) {
      entrada.listo = true;
      procesar();
    } else {
      entrada.timer = setTimeout(() => {
        entrada.timer = null;
        entrada.listo = true;
        procesar();
      }, opcionesRef.current.retardo);
    }
    publicar();
  }), [procesar, publicar]);

  // Campos del documento: se acumulan por bloque y se envían solo esos.
  // `conservar`: si el servidor los rechaza, se quedan pendientes para el
  // siguiente guardado del bloque (lo escrito sigue en pantalla).
  const programarCampos = useCallback((bloque, campos, { inmediato = false, conservar = true } = {}) => {
    const previo = camposRef.current.get(bloque);
    camposRef.current.set(bloque, {
      campos: { ...(previo ? previo.campos : {}), ...campos },
      conservar,
    });
    const tarea = async () => {
      const pendiente = camposRef.current.get(bloque);
      camposRef.current.delete(bloque);
      if (!pendiente || Object.keys(pendiente.campos).length === 0) return { data: undefined, sinCambios: true };
      enCursoRef.current = { clave: `campos:${bloque}`, campos: pendiente.campos };
      try {
        return await opcionesRef.current.enviarCampos(pendiente.campos);
      } catch (error) {
        if (esErrorDeConexion(error) || pendiente.conservar) {
          const nuevo = camposRef.current.get(bloque);
          camposRef.current.set(bloque, {
            campos: { ...pendiente.campos, ...(nuevo ? nuevo.campos : {}) },
            conservar: nuevo ? nuevo.conservar : pendiente.conservar,
          });
        }
        throw error;
      } finally {
        enCursoRef.current = null;
      }
    };
    return programar(`campos:${bloque}`, tarea, { inmediato });
  }, [programar]);

  const cancelar = useCallback((clave) => {
    const e = entradasRef.current.get(clave);
    if (!e) return;
    if (e.timer) clearTimeout(e.timer);
    entradasRef.current.delete(clave);
    e.esperas.forEach(w => w.resolve({ data: undefined, cancelado: true }));
    publicar();
  }, [publicar]);

  // Guarda ya todo lo pendiente. Resuelve true si ha quedado todo guardado.
  const guardarYa = useCallback(() => new Promise((resolve) => {
    entradasRef.current.forEach((e) => {
      if (e.timer) clearTimeout(e.timer);
      e.timer = null;
      e.listo = true;
    });
    if (reintentoRef.current) {
      clearTimeout(reintentoRef.current);
      reintentoRef.current = null;
    }
    if (entradasRef.current.size === 0 && !corriendoRef.current) {
      resolve(!sinConexionRef.current);
      return;
    }
    vacioRef.current.push(resolve);
    sinConexionRef.current = false;
    procesar();
  }), [procesar]);

  const hayPendientes = useCallback(
    () => entradasRef.current.size > 0 || corriendoRef.current,
    []
  );

  // Valor pendiente (o en curso) de un campo de un bloque: para no volver a
  // programar lo mismo
  const campoPendiente = useCallback((bloque, campo) => {
    const p = camposRef.current.get(bloque);
    if (p && Object.prototype.hasOwnProperty.call(p.campos, campo)) return { hay: true, valor: p.campos[campo] };
    const c = enCursoRef.current;
    if (c && c.clave === `campos:${bloque}` && Object.prototype.hasOwnProperty.call(c.campos, campo)) {
      return { hay: true, valor: c.campos[campo] };
    }
    return { hay: false };
  }, []);

  // Al salir de la página: manda con keepalive los campos pendientes. Devuelve
  // true si queda algo sin mandar (tareas que no son campos, sin conexión o en
  // curso), para que el navegador pregunte antes de salir.
  // `enviarOtro`: envío urgente de otro presupuesto (al cambiar de presupuesto
  // sin salir de la página, lo pendiente va al que se estaba editando).
  const vaciarUrgente = useCallback((enviarOtro = null) => {
    let quedan = corriendoRef.current && !enCursoRef.current;
    const urgente = enviarOtro || opcionesRef.current.enviarCamposUrgente;
    // Campos en curso: el PUT ya salió; si la página se cierra puede no llegar,
    // se vuelven a mandar con keepalive (el servidor aplica el mismo valor)
    const todos = {};
    if (enCursoRef.current) Object.assign(todos, enCursoRef.current.campos);
    camposRef.current.forEach((p) => { Object.assign(todos, p.campos); });
    if (Object.keys(todos).length > 0) {
      const enviado = urgente ? urgente(todos) : false;
      if (enviado) {
        camposRef.current.forEach((p, bloque) => {
          const clave = `campos:${bloque}`;
          const e = entradasRef.current.get(clave);
          if (e && e.timer) clearTimeout(e.timer);
          entradasRef.current.delete(clave);
        });
        camposRef.current.clear();
      } else {
        quedan = true;
      }
    }
    entradasRef.current.forEach((e, clave) => {
      if (!clave.startsWith('campos:')) quedan = true;
    });
    if (sinConexionRef.current) quedan = true;
    return quedan;
  }, []);

  useEffect(() => {
    montadoRef.current = true;
    const entradas = entradasRef.current;
    return () => {
      montadoRef.current = false;
      if (reintentoRef.current) clearTimeout(reintentoRef.current);
      reintentoRef.current = null;
      entradas.forEach((e) => { if (e.timer) clearTimeout(e.timer); });
    };
  }, []);

  return useMemo(() => ({
    estado,
    programar,
    programarCampos,
    cancelar,
    guardarYa,
    hayPendientes,
    campoPendiente,
    vaciarUrgente,
  }), [estado, programar, programarCampos, cancelar, guardarYa, hayPendientes, campoPendiente, vaciarUrgente]);
};

export default useAutoguardado;
